import { ReactElement, useState, useCallback, useEffect } from 'react'
import { AxiosError } from 'axios'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import RestoreIcon from '@mui/icons-material/Restore'
import SaveIcon from '@mui/icons-material/Save'
import {
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  SelectChangeEvent,
  styled,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { tapelineDtoToTapeline } from '@editor/stores/tapelineV2Store/utils'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button as StyledButton } from '@ui/actions'
import { useSnackbar } from '@ui/snackbar'
import {
  useFilterViewStore,
  useControlStore,
  useModelStore,
  useTapelineStore,
  roofStoreyKey,
  type FilterViewModel,
} from '@editorStores'
import { useStructuralPlanningSelection } from '@structuralPlanningHooks'
import { getFilterViews } from '@queries'
import {
  createFilterView,
  updateFilterView,
  deleteFilterView,
  setActiveFilterView,
} from '@mutations'
import { buildErrorMessage } from 'src/constants'
import { captureCurrentFilterView, applyFilterView } from '../VisibilityFilters/utils'

const StyledFormControl = styled(FormControl)({
  minWidth: 92,
})

const StyledSelect = styled(Select)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: 12,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.common.black, 0.23),
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.common.black, 0.38),
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  '& .MuiSelect-icon': {
    color: alpha(theme.palette.common.black, 0.54),
  },
  '& .MuiSelect-select': {
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(3.5),
  },
})) as unknown as typeof Select

const tapelinesForActiveFilterView = (
  filterViews: FilterViewModel[],
  activeFilterViewId: string,
) => {
  const activeView = filterViews.find(view => view.id === activeFilterViewId)
  return activeView?.tapelines.map(tapelineDtoToTapeline) ?? []
}

const FilterViewSelector = (): ReactElement => {
  const project = useModelStore(state => state.project)
  const projectGuid = project?.guid

  const savedFilterViews = useFilterViewStore(state => state.savedFilterViews)
  const activeFilterViewId = useFilterViewStore(state => state.activeFilterViewId)
  const setFromServer = useFilterViewStore(state => state.setFromServer)
  const clearFilterViews = useFilterViewStore(state => state.clear)
  const getDefaultFilterView = useFilterViewStore(state => state.getDefaultFilterView)
  const hiddenElementIds = useControlStore(state => state.hiddenElementIds)
  const setAllElementsVisible = useControlStore(state => state.setAllElementsVisible)
  const addHiddenElementIds = useControlStore(state => state.addHiddenElementIds)
  const visibleStoreys = useModelStore(state => state.visibleStoreys)
  const availableStoreys = useModelStore(state => state.availableStoreys)
  const visibilityByType = useModelStore(state => state.visibilityByType)
  const visibilityByTypeAndStorey = useModelStore(state => state.visibilityByTypeAndStorey)
  const setVisibleStoreys = useModelStore(state => state.setVisibleStoreys)
  const setTypeVisibility = useModelStore(state => state.setTypeVisibility)
  const setTypeVisibilityByStorey = useModelStore(state => state.setTypeVisibilityByStorey)
  const setActiveFilterViewIdOnTapelines = useTapelineStore(state => state.setActiveFilterViewId)
  const setTapelines = useTapelineStore(state => state.setLines)
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const {
    params: { storey: activeStorey },
  } = useStructuralPlanningSelection()

  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [newFilterViewName, setNewFilterViewName] = useState('Neue Ansicht')
  const [renameDialogOpen, setRenameDialogOpen] = useState(false)
  const [renameFilterViewName, setRenameFilterViewName] = useState('')

  const applyServerData = useCallback(
    (data: Awaited<ReturnType<typeof getFilterViews.request>>) => {
      setFromServer(data)
      setTapelines(tapelinesForActiveFilterView(data.filterViews, data.activeFilterViewId))
      setActiveFilterViewIdOnTapelines(data.activeFilterViewId)
      queryClient.setQueryData(getFilterViews.getKey(projectGuid), data)
    },
    [setFromServer, setTapelines, setActiveFilterViewIdOnTapelines, queryClient, projectGuid],
  )

  useQuery({
    queryKey: getFilterViews.getKey(projectGuid),
    queryFn: () => getFilterViews.request(projectGuid),
    enabled: !!projectGuid,
    onSuccess: data => {
      applyServerData(data)
    },
    onError: (error: AxiosError) => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Filteransichten'), {
        variant: 'error',
      })
      clearFilterViews()
      setTapelines([])
      setActiveFilterViewIdOnTapelines(null)
    },
  })

  const { mutateAsync: runCreateFilterView } = useMutation(
    (payload: Parameters<typeof createFilterView.request>[1]) =>
      createFilterView.request(projectGuid, payload),
    {
      onSuccess: applyServerData,
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern der Filteransicht'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutateAsync: runUpdateFilterView } = useMutation(
    ({
      filterViewId,
      payload,
    }: {
      filterViewId: string
      payload: Parameters<typeof updateFilterView.request>[2]
    }) => updateFilterView.request(projectGuid, filterViewId, payload),
    {
      onSuccess: applyServerData,
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Aktualisieren der Filteransicht'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutateAsync: runDeleteFilterView } = useMutation(
    (filterViewId: string) => deleteFilterView.request(projectGuid, filterViewId),
    {
      onSuccess: applyServerData,
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Löschen der Filteransicht'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutateAsync: runSetActiveFilterView } = useMutation(
    (filterViewId: string) => setActiveFilterView.request(projectGuid, filterViewId),
    {
      onSuccess: applyServerData,
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Wechseln der Filteransicht'), {
          variant: 'error',
        })
      },
    },
  )

  const activeFilterView = savedFilterViews.find(view => view.id === activeFilterViewId) ?? null
  const defaultFilterView = getDefaultFilterView()
  const isDefaultView = activeFilterView?.isDefault ?? true

  useEffect(() => {
    if (!activeFilterView || activeFilterView.isDefault) return

    applyFilterView(
      activeFilterView,
      {
        setAllElementsVisible,
        addHiddenElementIds,
      },
      {
        setVisibleStoreys,
        setTypeVisibility,
        setTypeVisibilityByStorey,
      },
    )
  }, [
    activeFilterView,
    setAllElementsVisible,
    addHiddenElementIds,
    setVisibleStoreys,
    setTypeVisibility,
    setTypeVisibilityByStorey,
  ])

  const handleViewChange = useCallback(
    async (event: SelectChangeEvent<string>) => {
      const newViewId = event.target.value
      const newFilterView = savedFilterViews.find(view => view.id === newViewId)
      if (newFilterView && !newFilterView.isDefault) {
        applyFilterView(
          newFilterView,
          {
            setAllElementsVisible,
            addHiddenElementIds,
          },
          {
            setVisibleStoreys,
            setTypeVisibility,
            setTypeVisibilityByStorey,
          },
        )
      }
      if (projectGuid) {
        await runSetActiveFilterView(newViewId)
      }
    },
    [
      savedFilterViews,
      setAllElementsVisible,
      addHiddenElementIds,
      setVisibleStoreys,
      setTypeVisibility,
      setTypeVisibilityByStorey,
      projectGuid,
      runSetActiveFilterView,
    ],
  )

  const handleSaveFilterView = useCallback(async () => {
    if (!newFilterViewName.trim() || !projectGuid || !activeFilterViewId) return

    const filterViewData = captureCurrentFilterView(
      activeStorey || null,
      hiddenElementIds,
      visibleStoreys,
      visibilityByType,
      visibilityByTypeAndStorey,
    )

    await runCreateFilterView({
      name: newFilterViewName.trim(),
      ...filterViewData,
      duplicateTapelinesFromFilterViewId: activeFilterViewId,
      setActive: true,
    })

    setNewFilterViewName('Neue Ansicht')
    setSaveDialogOpen(false)
  }, [
    newFilterViewName,
    activeStorey,
    projectGuid,
    activeFilterViewId,
    hiddenElementIds,
    visibleStoreys,
    visibilityByType,
    visibilityByTypeAndStorey,
    runCreateFilterView,
  ])

  const handleOpenRenameDialog = useCallback(() => {
    if (activeFilterView) {
      setRenameFilterViewName(activeFilterView.name)
      setRenameDialogOpen(true)
    }
  }, [activeFilterView])

  const handleRenameFilterView = useCallback(async () => {
    if (!renameFilterViewName.trim() || !activeFilterViewId) return

    await runUpdateFilterView({
      filterViewId: activeFilterViewId,
      payload: { name: renameFilterViewName.trim() },
    })
    setRenameDialogOpen(false)
    setRenameFilterViewName('')
  }, [renameFilterViewName, activeFilterViewId, runUpdateFilterView])

  const handleUpdateCurrentFilterView = useCallback(async () => {
    if (!activeFilterViewId || isDefaultView || !projectGuid) return

    const filterViewData = captureCurrentFilterView(
      activeStorey || null,
      hiddenElementIds,
      visibleStoreys,
      visibilityByType,
      visibilityByTypeAndStorey,
    )

    await runUpdateFilterView({
      filterViewId: activeFilterViewId,
      payload: filterViewData,
    })

    enqueueSnackbar('Ansicht erfolgreich gespeichert', { variant: 'success' })
  }, [
    activeFilterViewId,
    isDefaultView,
    activeStorey,
    projectGuid,
    hiddenElementIds,
    visibleStoreys,
    visibilityByType,
    visibilityByTypeAndStorey,
    runUpdateFilterView,
    enqueueSnackbar,
  ])

  const handleDeleteCurrentFilterView = useCallback(async () => {
    if (!activeFilterViewId || isDefaultView || !projectGuid) return

    await runDeleteFilterView(activeFilterViewId)
    enqueueSnackbar('Ansicht erfolgreich gelöscht', { variant: 'success' })
  }, [activeFilterViewId, isDefaultView, projectGuid, runDeleteFilterView, enqueueSnackbar])

  const handleResetToDefault = useCallback(() => {
    if (!projectGuid) return

    const allElementTypes: ElementTypes[] = [
      'beams',
      'columns',
      'inner_walls',
      'lintels',
      'outer_walls',
      'purlins',
      'roof_slabs',
      'rips',
      'slabs',
      'vertical_roof_slabs',
      'vertical_slabs',
      'slab_beams',
      'roof_slab_beams',
      'standard_rips',
      'foundation',
    ]

    setVisibleStoreys(Array.from(availableStoreys))

    allElementTypes.forEach(type => {
      if (type === 'foundation' || type === 'slab_beams' || type === 'roof_slab_beams') {
        setTypeVisibility(type, false)
      } else {
        setTypeVisibility(type, true)
      }
    })

    const availableNonRoofStoreys = Array.from(availableStoreys).filter(
      storey => storey !== roofStoreyKey,
    )
    allElementTypes.forEach(type => {
      setTypeVisibilityByStorey(type, availableNonRoofStoreys)
    })

    enqueueSnackbar('Sichtbarkeitsfilter zurückgesetzt', { variant: 'success' })
  }, [
    projectGuid,
    availableStoreys,
    setVisibleStoreys,
    setTypeVisibility,
    setTypeVisibilityByStorey,
    enqueueSnackbar,
  ])

  const canModifyActiveView = activeFilterView?.isModifiable ?? false
  const canDeleteActiveView = activeFilterView?.isDeletable ?? false

  return (
    <>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 400,
            color: 'text.secondary',
            userSelect: 'none',
            whiteSpace: 'nowrap',
            mr: 0.25,
          }}
        >
          Ansicht
        </Typography>
        <StyledFormControl size="small">
          <StyledSelect
            id="filter-view-select"
            value={activeFilterViewId || defaultFilterView?.id || ''}
            onChange={handleViewChange}
          >
            {savedFilterViews.map(view => (
              <MenuItem key={view.id} value={view.id}>
                {view.name}
              </MenuItem>
            ))}
          </StyledSelect>
        </StyledFormControl>
        <Stack direction="row">
          {canModifyActiveView && !isDefaultView && (
            <Tooltip title="Ansicht umbenennen" placement="bottom" arrow disableInteractive>
              <StyledButton
                size="small"
                variant="outlined"
                onClick={handleOpenRenameDialog}
                aria-label="Ansicht umbenennen"
                data-cy="btn-rename-filter-view"
              >
                <EditIcon fontSize="small" color="inherit" />
              </StyledButton>
            </Tooltip>
          )}
          {canModifyActiveView && !isDefaultView && (
            <Tooltip title="Ansicht aktualisieren" placement="bottom" arrow disableInteractive>
              <StyledButton
                size="small"
                variant="outlined"
                onClick={handleUpdateCurrentFilterView}
                aria-label="Ansicht aktualisieren"
                data-cy="btn-update-filter-view"
              >
                <SaveIcon fontSize="small" color="inherit" />
              </StyledButton>
            </Tooltip>
          )}
          {canDeleteActiveView && !isDefaultView && (
            <Tooltip title="Ansicht löschen" placement="bottom" arrow disableInteractive>
              <StyledButton
                size="small"
                variant="outlined"
                onClick={handleDeleteCurrentFilterView}
                aria-label="Ansicht löschen"
                data-cy="btn-delete-filter-view"
              >
                <DeleteIcon fontSize="small" color="inherit" />
              </StyledButton>
            </Tooltip>
          )}
          <Tooltip title="Neue Ansicht erstellen" placement="bottom" arrow disableInteractive>
            <StyledButton
              size="small"
              variant="outlined"
              onClick={() => setSaveDialogOpen(true)}
              aria-label="Neue Ansicht erstellen"
              data-cy="btn-create-filter-view"
            >
              <AddIcon fontSize="small" color="inherit" />
            </StyledButton>
          </Tooltip>
          <Tooltip
            title="Sichtbarkeitsfilter zurücksetzen"
            placement="bottom"
            arrow
            disableInteractive
          >
            <StyledButton
              size="small"
              variant="outlined"
              onClick={handleResetToDefault}
              aria-label="Sichtbarkeitsfilter zurücksetzen"
              data-cy="btn-reset-filter-view"
            >
              <RestoreIcon fontSize="small" color="inherit" />
            </StyledButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <DialogTitle>Ansicht speichern</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name der Ansicht"
            fullWidth
            variant="outlined"
            value={newFilterViewName}
            onChange={e => setNewFilterViewName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                handleSaveFilterView()
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={handleSaveFilterView}
            variant="contained"
            disabled={!newFilterViewName.trim()}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)}>
        <DialogTitle>Ansicht umbenennen</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name der Ansicht"
            fullWidth
            variant="outlined"
            value={renameFilterViewName}
            onChange={e => setRenameFilterViewName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                handleRenameFilterView()
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={handleRenameFilterView}
            variant="contained"
            disabled={!renameFilterViewName.trim()}
          >
            Umbenennen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default FilterViewSelector
